import React, { useEffect, useState } from 'react';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import "../../css/App.css";
import { useSelector, useDispatch } from 'react-redux';
import termsOfUse from "../../img/TermsOfUse.pdf"

const SubscriptionForm = ({customerId, hasSavedPaymentMethod}) => {
  const [couponValid, setCouponValid] = useState(null);
  const [couponChecking, setCouponChecking] = useState(false);
  const [discountedAmount, setDiscountedAmount] = useState(null);
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState('')
  const paymentMethodAvailable = useSelector(state => state.user.stripeCustomer?.invoice_settings?.default_payment_method)
  const [hasSubscription, setHasSubscription] = useState(false)
  const [customer, setCustomer] = useState();
  const [customerResponded, setCustomerResponded] = useState(false)
  const [subscriptionResponded, setSubscriptionResponded] = useState(false)
  const [subscription, setSubscription] = useState();
  const [loading, setLoading] = useState(false); // New state for loading
  const [subscriptionType, setSubscriptionType] = useState('monthly');
  const [showAttention, setShowAttention] = useState(false);
  const [couponCode, setCouponCode] = useState("")
  const [invoiceDetails, setInvoiceDetails] = useState()
  const [invoiceResponded, setInvoiceResponded] = useState(false)
  const [comps, setComps] = useState()
  const [comparisonsResponded, setComparisonsResponded] = useState(false)
  const [setToCancel, setSetToCancel] = useState(false)
  const [amount, setAmount] = useState(0)
  const [description, setDescription] = useState("")
  const [subscribeClicked, setSubscribeClicked] = useState(false)
  const [calculateResponded, setCalculateResponded] = useState(false)
  const [count, setCount] = useState(0)  
  const [originalAmount, setOriginalAmount] = useState()
  const [duration, setDuration] = useState("")
  const [invalidAttempts, setInvalidAttempts] = useState(0)
  const userData = useSelector(state => state.user)

  const dispatch = useDispatch();

  useEffect(() => {
    if (!userData?.email) return;

    const intervalId = setInterval(() => {
      getCustomerDetails(userData.email);
    }, 4000); // 4 seconds

    return () => clearInterval(intervalId);
  }, [userData?.email, invoiceDetails, customerId]);

  // Main logic useEffect
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (couponCode?.trim()) {
        validateCoupon(couponCode);
      } else {
        setCouponValid(null);
        setDiscountedAmount(null);
      }
    }, 1000);

    if (couponValid === false) {
      setInvalidAttempts(prev => prev + 1);
    }

    const attentionTimeout = setTimeout(() => {
      setShowAttention(true);
    }, 250);

    if (!comparisonsResponded) {
      getResults(userData._id);
    }

    if (subscription?.data?.[0] && calculateResponded) {
      updateUserSubscription();
    }

    const customerId = subscription?.[0]?.customer ?? null;
    getInvoiceDetails(customerId);
    setInvoiceResponded(true);
  

    return () => {
      clearTimeout(delayDebounce);
      clearTimeout(attentionTimeout);
    };
  }, [
    userData,
    userData?.stripeCustomer,
    dispatch,
    customerResponded,
    comparisonsResponded,
    subscription?.data,
    calculateResponded,
    couponCode,
    subscriptionType,
    invoiceResponded, 
    couponValid,
    paymentMethodAvailable,
    hasSavedPaymentMethod,
    subscription
  ]);
  const validateCoupon = async (code) => {
    let priceId;
  
    switch (subscriptionType) {
      case "monthly":
        priceId = process.env.REACT_APP_PRICE_MONTHLY;
        break;
      case "yearly":
        priceId = process.env.REACT_APP_PRICE_YEARLY;
        break;
      case "hybrid":
        priceId = process.env.REACT_APP_PRICE_HYBRID;
        break;
      default:
        console.error("Unknown subscriptionType:", subscriptionType);
        return;
    }
  
    try {
      const response = await axios.post(process.env.REACT_APP_VALIDATE_COUPON, {
        code,
        price_id: priceId,
      });
  
      const data = response.data;
  
      if (data.valid) {
        setCouponValid(true);
        setDiscountedAmount(data.final_amount / 100);
        setDuration(data.duration);
      } else {
        setCouponValid(false);
        setDiscountedAmount(null);
      }
    } catch (err) {
      console.error("Error validating coupon:", err);
      setCouponValid(false);
      setDiscountedAmount(null);
    }
  };
  
  
  const getCustomerDetails = async (email) => {
    try {
      const customerResponse = await axios.get(`${process.env.REACT_APP_CUSTOMER_EMAIL}`, {
        params: { email },
      });
  
      const customer = customerResponse.data;
  
      if (userData && userData._id) {
        // First preference: userData.stripeCustomer.id
        // Fallback: customer.id from email lookup
        const whichCustomerId = userData?.stripeCustomer?.id || customerId;
  
        if (whichCustomerId) {
          setCustomer(customer);
          setCustomerResponded(true);
  
          const subscriptionResponse = await axios.get(
            `${process.env.REACT_APP_GET_CUSTOMER_SUBSCRIPTIONS}${whichCustomerId}`
          );
  
          const subscriptionData = subscriptionResponse.data.subscriptions;
          setSubscription(subscriptionData);
          setSubscriptionResponded(true);
        } else {
          console.warn("No Stripe customer ID found for this user.");
        }
      } else {
        console.error("User data or user ID is not defined");
      }
    } catch (error) {
      console.error("Error in getCustomerDetails: ", error);
    }
  };
  
  
const updateUserSubscription = async () => {
  const update = axios.post(process.env.REACT_APP_UPDATE_USER_SUBSCRIPTION, {
    user: userData._id,
    created: subscription?.data[0]?.created,
    start: subscription?.data[0]?.current_period_start,
    end: subscription?.data[0]?.current_period_end,
    used: count,
  })
}

function readableDate(date) {
  if (date !== undefined && date !== null) {
    const readable = new Date(date * 1000).toLocaleDateString();
    return readable;
  } else {
    return "N/A";
  }
}

const handleSubmit = async (event) => {
  event.preventDefault();

  if (subscribeClicked) return; // Prevent duplicate submissions

  setSubscribeClicked(true);
  setLoading(true);

  if (!stripe || !elements) {
    setLoading(false);
    return;
  }

  // Confirm if user wants to proceed without a coupon
  if (!couponCode?.trim()) {
    const confirmApplyWithoutCoupon = true;
    if (!confirmApplyWithoutCoupon) {
      setLoading(false);
      return;
    }
  }

  // If base subscription already exists, exit early
  // Choose the appropriate priceId based on subscriptionType
  let priceId;
  switch (subscriptionType) {
    case "monthly":
      priceId = process.env.REACT_APP_PRICE_MONTHLY;
      break;
    case "hybrid":
      priceId = process.env.REACT_APP_PRICE_HYBRID;
      break;
    case "yearly":
      priceId = process.env.REACT_APP_PRICE_YEARLY;
      break;
    default:
      priceId = process.env.REACT_APP_PRICE_MONTHLY; // fallback
  }
  try {
    const subscriptionResponse = await axios.post(
      process.env.REACT_APP_CREATE_BASE_SUBSCRIPTION,
      {
        email: userData?.email,
        customerId: userData?.stripeCustomer?.id,
        paymentMethodId:
          userData?.stripeCustomer?.invoice_settings?.default_payment_method,
        priceId,
        coupon: couponCode,
      }
    );
    setInvoiceResponded(false)
    dispatch({
      type: 'SET_STRIPE_SUBSCRIPTION',
      payload: subscriptionResponse.data.data,
    });
  } catch (err) {
    const errorMessage =
      err.response?.data?.error?.message || "An unexpected error occurred.";
    setErrorMessage(errorMessage);
    console.error("Error creating subscription:", err);

    const retry = window.confirm(
      "That coupon does not exist. Would you like to try again?"
    );
    if (!retry) window.location.reload();
  } finally {
    setLoading(false);
    getCustomerDetails(userData?.email);
  }
};


const handleRetry = async (e) => {
  e.preventDefault();
  setLoading(true);

  try {
    const { data } = await axios.post(process.env.REACT_APP_RETRY_SUBSCRIPTION, {
      subscriptionId: userData.baseSubscription.id,
    });

    if (data.success) {
      setInvoiceResponded(false)
      dispatch({
        type: 'SET_STRIPE_SUBSCRIPTION',
        payload: data.subscription,
      });
      // Show success UI
    } else {
      console.error("Payment retry failed:", data.errorMessage);
      // Show error UI
    }
  } catch (error) {
    console.error("Retry subscription error:", error);
    // Show error UI
  } finally {
    setLoading(false);
  }
};


const handleCancel = async (id) => {
  if (!userData?.baseSubscription?.id) {
    alert("No subscription found to cancel.");
    return;
  }
  setInvoiceResponded(false)
  try {
    const cancelResponse = await axios.post(
      `${process.env.REACT_APP_CANCEL_SUBSCRIPTION}/${id}`
    );
    // Update Redux with the canceled subscription
    dispatch({
      type: 'SET_STRIPE_SUBSCRIPTION',
      payload: cancelResponse.data,
    });

    // Optionally refetch customer details
    if (cancelResponse.data) {
      await getCustomerDetails(userData.email);
    }
  } catch (err) {
    console.error("Error canceling subscription:", err);
    alert("Failed to cancel subscription. Please try again.");
  }
};

function calculateAdditionalBill() {
  if(userData?.baseSubscription?.status != "trialing") {
    const periodStart = userData?.baseSubscription?.current_period_start;
  const periodEnd = userData?.baseSubscription?.current_period_end;
  let updatedCount = count; // Initialize with current count value
  if (comps && comps.length > 0) {
    for (let i = 0; i < comps.length; i++) {
      let date = new Date(comps[i].dateUploaded);
      let utcDate = Math.floor((date.getTime() + (68800 * 1000)) / 1000);
      if (utcDate > periodStart && utcDate < periodEnd && comps[i].status === "Finished") {
        updatedCount++; // Increment count using a temporary variable
      }
    }
  }
  if ((updatedCount - userData?.monthly_allotment) > 0) {
    const calculatedAmount = (updatedCount - userData?.monthly_allotment) * userData?.additional_cost;
    setAmount(calculatedAmount);
    const writeDescription = `You used ${updatedCount - userData?.monthly_allotment} additional comparisons this billing cycle @ $${userData?.additional_cost} per comparison. Total: ${calculatedAmount}`;
    setDescription(writeDescription);
  }
  setCount(updatedCount); // Update count state with the final value
  setCalculateResponded(true);
  }
}
const getResults = async (id) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_RESULTS_DATA}user/${id}`);
    const data = await response.json();
    setComps(data);
    setComparisonsResponded(true)
  } catch (error) {
    console.error("Error fetching results:", error);
  }
}
const enabledStep = {
  margin: '6px 0px',
  textTransform: 'uppercase',
  color: '#00B3B5',
  fontWeight: '800',
  fontSize: '24px',
  padding: '0'
}
const disabledStep = {
  margin: '6px 0px',
  padding: '0',
  textTransform: 'uppercase',
  color: '#AAAAAA',
  fontSize: '18px',
  fontWeight: '800'
}
function checkUserState() {
  if (userData?.coupon != "") {
    return true
  }
  else {
    return false
  }
}
const getInvoiceDetails = async (id) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_INVOICE_DETAILS}/${id}`);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const data = await response.json();
    setInvoiceDetails(data);
  } catch (error) {
    console.error("Error fetching invoice details:", error);
  }
};

function returnForms() {
  const monthlyPrice = parseFloat(process.env.REACT_APP_MONTHLY_PRICE || '399');
  const hybridPrice = parseFloat(process.env.REACT_APP_HYBRID_PRICE || '359');
  const yearlyPrice = parseFloat(process.env.REACT_APP_YEARLY_PRICE || '3591');

  const originalAmount =
    subscriptionType === 'yearly'
      ? yearlyPrice
      : subscriptionType === 'hybrid'
      ? hybridPrice
      : monthlyPrice;

  const commonInputStyle = {
    width: '100%',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    marginBottom: '10px',
    fontStyle: 'italic',
  };

  const commonButtonStyle = {
    marginTop: '12px',
    padding: '10px 20px',
    backgroundColor: '#3ba3a3',
    color: '#fff',
    border: 'none',
    fontWeight: 'bold',
    cursor: loading ? 'not-allowed' : 'pointer',
  };

  const priceInfo = (
    <div>
      {subscriptionType === 'yearly' && !couponValid && (
        <div style={{ textDecoration: 'line-through', color: '#888' }}>
          Original Price: $4788
        </div>
      )}
      {subscriptionType === 'hybrid' && !couponValid && (
        <div style={{ textDecoration: 'line-through', color: '#888' }}>
          Original Price: $399
        </div>
      )}
      <div
        style={{
          margin: '12px 0',
          color: couponValid ? '#888' : '#1a7f37',
          fontWeight: 'bold',
          fontSize: '16px',
          fontStyle: couponValid ? 'italic' : 'normal',
          textDecoration: couponValid ? 'line-through' : 'none',
        }}
      >
        {subscriptionType === 'monthly' && <>Price: ${monthlyPrice.toFixed(2)}</>}
        {subscriptionType === 'hybrid' && (
          <>
            Discounted Price: ${hybridPrice.toFixed(2)} (10% OFF) – Annual Contract. Paid Monthly
          </>
        )}
        {subscriptionType === 'yearly' && <>Discounted Price: ${yearlyPrice.toFixed(2)} (25% OFF)</>}
      </div>

      {couponChecking && <div>Checking coupon...</div>}

      {!couponChecking && couponValid === false && (
        <div style={{ color: 'red' }}>Invalid coupon code.</div>
      )}

      {!couponChecking && couponValid && discountedAmount !== null && (
        <>
          <div style={{ margin: '8px 0', color: 'green', fontWeight: 'bold' }}>
            Coupon confirmed! Your Price: ${discountedAmount.toFixed(2)}
          </div>
          <div style={{ margin: '8px 0', color: 'green', fontWeight: 'bold' }}>
            Savings: ${(originalAmount - discountedAmount).toFixed(2)}
          </div>
          <div style={{ margin: '8px 0', color: 'green', fontWeight: 'bold' }}>
            Duration: <strong style={{ textTransform: 'uppercase' }}>{duration}</strong>!
          </div>
        </>
      )}
    </div>
  );

  const labels = {
    monthly: "Go Monthly",
    hybrid: "Smart Advantage",
    yearly: "Pro Annual",
  };
  
  const subscriptionSelector = (
    <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
      {['monthly', 'hybrid', 'yearly'].map(type => (
        <div
          key={type}
          onClick={() => {
            setSubscriptionType(type);
            setCouponValid(false);
          }}
          style={{
            flex: 1,
            padding: '10px 0',
            textAlign: 'center',
            borderRadius: '8px',
            cursor: 'pointer',
            backgroundColor: subscriptionType === type ? '#00B3B5' : '#ccc',
            color: subscriptionType === type ? '#fff' : '#333',
            fontWeight: subscriptionType === type ? 'bold' : 'normal',
            transition: 'background-color 0.3s, color 0.3s',
          }}
        >
          {labels[type]}
        </div>
      ))}
    </div>
  );
  

  // NO saved payment method
  if (!hasSavedPaymentMethod) {
    return (
      <form className={`subscribe-form ${showAttention ? 'next-step' : ''}`} onSubmit={handleSubmit}>
        <p style={disabledStep}>Step 2</p>

        {subscriptionSelector}

        <input
          type="text"
          placeholder="Coupon Code"
          value={couponCode}
          disabled={checkUserState() || invalidAttempts >= 50}
          onChange={(e) => setCouponCode(e.target.value)}
          style={{
            ...commonInputStyle,
            backgroundColor: invalidAttempts >= 50 ? '#fdd' : 'white',
          }}
        />

        {invalidAttempts >= 50 && (
          <div style={{ color: 'red' }}>
            Too many failed attempts. Please try again later.
          </div>
        )}

        {priceInfo}

        <button
          className="green-button"
          type="submit"
          disabled={!stripe || loading}
          style={commonButtonStyle}
        >
          {loading ? 'Processing...' : 'Subscribe'}
        </button>
      </form>
    );
  }

  // Saved payment method but no active subscription
  if (
    (!subscription || !subscription.some(sub => sub?.status === 'active' || sub?.status === 'trialing')) &&
    hasSavedPaymentMethod
  ) {
    return (
      <form className={`subscribe-form ${showAttention ? 'attention-grabber' : ''}`} onSubmit={handleSubmit}>
        <p style={enabledStep}>STEP 2</p>

        {subscriptionSelector}

        <input
          type="text"
          placeholder="Coupon Code"
          value={couponCode}
          disabled={checkUserState() || invalidAttempts >= 50}
          onChange={(e) => setCouponCode(e.target.value)}
          style={{
            ...commonInputStyle,
            backgroundColor: invalidAttempts >= 50 ? '#fdd' : 'white',
          }}
        />

        {invalidAttempts >= 50 && (
          <div style={{ color: 'red', marginBottom: '10px' }}>
            Too many failed attempts. Please try again later.
          </div>
        )}

        {priceInfo}

        <button
          className="green-button"
          type="submit"
          disabled={!stripe || loading}
          style={commonButtonStyle}
        >
          {loading ? 'Processing...' : 'Subscribe'}
        </button>
        <br />
        <br />
        <b>
          By clicking "Subscribe" you agree to our &nbsp;
          <a 
            href={termsOfUse}
            target="_blank"
          >
            Terms of Use
          </a>.
        </b>

      </form>
    );
  }

  // Payment incomplete
  if (subscription?.status === 'incomplete') {
    return (
      <form className={`subscribe-form ${showAttention ? 'attention-grabber' : ''}`} onSubmit={handleRetry}>
        <div
          style={{
            backgroundColor: '#fff3cd',
            color: '#856404',
            padding: '12px 16px',
            borderLeft: '4px solid #ffeeba',
            borderRadius: '4px',
            marginBottom: '16px',
          }}
        >
          <p style={{ margin: 0, fontWeight: 'bold' }}>Payment Incomplete</p>
          <p style={{ margin: 0, fontSize: '0.9em' }}>
            Looks like your initial subscription payment didn't go through. Double-check your credit card info, and maybe give your bank a call.
          </p>
          <p>Happy LEVLRing!</p>
        </div>

        <button
          className="green-button"
          type="submit"
          disabled={loading}
          style={commonButtonStyle}
        >
          {loading ? 'Processing...' : 'Retry Payment'}
        </button>
      </form>
    );
  }

  return null;
}

  return (
    <div>
      {returnForms()}
      {loading && <div className="loading-spinner"></div>}
      <div className="price-model">
      {subscription?.some(sub => sub.status !== 'canceled') && (
        <div
          className="subscription-container"
          style={{
            maxHeight: '250px',
            overflowY: 'auto',
            border: '1px solid #ccc',
            padding: '10px',
            borderRadius: '8px',
            marginTop: '20px',
          }}
        >
          {subscription
            .filter(sub => sub.status !== 'canceled')
            .map(sub => (
              <div
                key={sub.id}
                className="subscription-card"
                style={{ borderBottom: '1px solid #eee', padding: '10px 0' }}
              >
                <h4>Subscription ID: {sub.id}</h4>
                <p>
                  <strong>Status:</strong> {sub.status}
                </p>
                <p>
                  <strong>Next Billing Date:</strong>{' '}
                  {sub.current_period_end
                    ? new Date(sub.current_period_end * 1000).toLocaleDateString()
                    : 'N/A'}
                </p>
                
                <p>
                  <strong>Created:</strong>{' '}
                  {sub.created
                    ? new Date(sub.created * 1000).toLocaleDateString()
                    : 'N/A'}
                </p>

                {['active', 'trialing'].includes(sub.status) && !sub.cancel_at && (
                  <button
                    onClick={() => handleCancel(sub.id)}
                    style={{
                      backgroundColor: '#ff4d4f',
                      color: '#fff',
                      padding: '5px 10px',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel Subscription
                  </button>
                )}

                {sub.cancel_at && (
                  <p style={{ color: 'red' }}>
                    Scheduled to cancel on{' '}
                    {new Date(sub.cancel_at * 1000).toLocaleDateString()}
                  </p>
                )}
              </div>
            ))}
        </div>
      )}

      {(subscription?.some(sub => sub?.status === 'active' || sub?.status === 'trialing')) && (
        <div>
          <p>Next Bill</p>
          <strong>{readableDate(invoiceDetails?.invoice?.period_end)}:</strong> $
          <i>{typeof invoiceDetails?.invoice?.amount_due === 'number'
            ? (invoiceDetails.invoice.amount_due / 100).toFixed(2)
            : '0.00'}
          </i>
        </div>
      )}
      </div>
    </div>
  );
};
export default SubscriptionForm;

